import { createExerciseEvaluator, getSequencerConfig, isMetronomePractice, isSheetPractice } from '@ezchops/game/lib/practice';
import { isSequencerExercise, isTrackSetExercise } from '@ezchops/model/lib/lesson';
import { generateMetronomeSheet } from '@ezchops/musicsheet';
import React, { useCallback, useEffect, useReducer } from 'react';
import { useAudioContext } from '../../../contexts';
export const ExerciseContext = React.createContext(null);
export function ExerciseContextProvider(props) {
    const audioContext = useAudioContext();
    const reduceExerciseState = useCallback((state, action) => exerciseContextState(reduceExerciseOptions(state, action), audioContext), [audioContext]);
    const [contextState, dispatch] = useReducer(reduceExerciseState, exerciseContextState(initialState(props.exercise), audioContext));
    const setTempo = React.useCallback((tempo) => dispatch({ type: 'tempo', tempo }), []);
    const setMetronome = React.useCallback((metronome) => dispatch({ type: 'metronome', metronome }), []);
    const setExercise = React.useCallback((exercise) => dispatch({ type: 'exercise', exercise }), []);
    useEffect(() => {
        setExercise(props.exercise);
    }, [props.exercise, setExercise]);
    const context = { ...contextState, setTempo, setMetronome, setExercise };
    return React.createElement(ExerciseContext.Provider, { value: context }, props.children);
}
function initialState(exercise) {
    return {
        exercise,
        tempo: exercise.defaultTempo,
        metronome: exercise.metronomeSheet != null ||
            !(isTrackSetExercise(exercise) && exercise.track.hasMetronome)
    };
}
function reduceExerciseOptions(state, action) {
    switch (action.type) {
        case 'exercise':
            const { exercise } = action;
            return initialState(exercise);
        case 'tempo':
            const { tempo } = action;
            return {
                ...state,
                tempo
            };
        case 'metronome':
            const { metronome } = action;
            return {
                ...state,
                metronome
            };
    }
}
function exerciseContextState(options, audioContext) {
    const { exercise, tempo, metronome } = options;
    const evaluator = createExerciseEvaluator(exercise, tempo);
    const metronomeSheet = metronome ? getMetronomeSheet(exercise, evaluator, metronome) : null;
    const sequencerConfig = getSequencerConfig(exercise, tempo, evaluator.timing) ?? null;
    return {
        exercise, tempo, metronome, evaluator, metronomeSheet, sequencerConfig
    };
}
export function availableTempos(exercise) {
    if (isTrackSetExercise(exercise)) {
        return exercise.track.tracks.map(track => track.tempo);
    }
    else if (isSequencerExercise(exercise)) {
        return exercise.track.stems.tempos;
    }
    return { min: 40, max: 240 };
}
export function useExerciseContext() {
    const context = React.useContext(ExerciseContext);
    if (context == null) {
        throw new Error('No exercise context');
    }
    return context;
}
export function getMetronomeSheet(exercise, evaluator, metronome) {
    const { practice } = evaluator;
    if (exercise.metronomeSheet != null) {
        return exercise.metronomeSheet;
    }
    if (isTrackSetExercise(exercise) && exercise.track.hasMetronome && (metronome != true)) {
        return null;
    }
    if (isSheetPractice(practice)) {
        return generateMetronomeSheet(practice.sheet, false, evaluator.timing.tickNote);
    }
    if (isMetronomePractice(practice)) {
        return generateMetronomeSheet({ segments: [{ signature: evaluator.timing.signature, measures: [], repeats: null }] }, false, evaluator.timing.tickNote);
    }
    return null;
}
//# sourceMappingURL=ExerciseContext.js.map